import { type FC, type RefObject, useCallback, useMemo, useState } from 'react';
import {
  HeaderSizedCell,
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import IconButtonAbsoluteMenu from '__components/Tables/Big/IconButtonAbsoluteMenu';
import { colorsButton } from '__app/colors';
import Icon from '__components/Kizen/Icon';
import { type TFunction, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceAccountsInfiniteQuery } from 'query/service-account';
import { runToolsAsOptionsQuery } from 'query/automation';
import TeamMemberPermissionGroups from 'components/Fields/InlineTableFieldInput/TeamMemberPermissionGroups';
import TextInputInline from '__components/Inputs/inline/TextInput';
import ServiceAccountService from 'services/ServiceAccountService';
import { StyledActionSizedCell, StatusDot, StatusCell } from './styles';
import type { MenuAction } from './types';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { KizenTypography } from '__app/typography';
import type { AccessStruct } from '@kizen/permissions/types';
import type { ServiceAccount } from '@kizen/api/service-account';
import styled from '@emotion/styled';
import { PluginIconIndicator } from 'ts-components/Plugins/PluginIconIndicator';

const emptyLongDash = '—';

const StyledNameCellContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const PLUGIN_ACCOUNT_TYPES = [
  'service_automationplugin',
  'service_integration_plugin',
];

const isPluginServiceAccount = (account: ServiceAccount): boolean =>
  PLUGIN_ACCOUNT_TYPES.includes(account.account_type as string);

// api_name templates:
//   service_automationplugin:   {plugin_api_name}__service_automation
//   service_integration_plugin: {plugin_api_name}__{service_account_name}
// In both cases the plugin api name is the segment before the first '__'.
const getPluginApiName = (account: ServiceAccount): string => {
  const apiName = account.api_name ?? '';
  const idx = apiName.indexOf('__');
  return idx !== -1 ? apiName.slice(0, idx) : '';
};

type UseColumnsProps = {
  onSelectAction: (action: MenuAction, account: ServiceAccount) => void;
  access: AccessStruct;
  tableWrapperRef: RefObject<HTMLDivElement>;
};

const COLUMN_WIDTH = {
  name: '220px',
  type: '110px',
  status: '80px',
  apiName: '160px',
  token: '150px',
  permissionGroups: '180px',
  date: '160px',
  actions: '51px',
  padding: '20px',
};

const OPTION_HEIGHT = 32;

const accountTypeLabels: Record<string, (t: TFunction) => string> = {
  service_automation: (t: TFunction) => t('Agentic Workflow'),
  service_automationplugin: (t: TFunction) => t('App Agentic Workflow'),
  service_integration: (t: TFunction) => t('Integration'),
  service_integration_plugin: (t: TFunction) => t('App Integration'),
};

const statusLabels = {
  active: (t: TFunction) => t('Active'),
  disabled: (t: TFunction) => t('Disabled'),
  suspended: (t: TFunction) => t('Suspended'),
};

const getAccountTypeLabel = (
  type: ServiceAccount['account_type'] | string,
  t: TFunction
) => {
  return accountTypeLabels[type]?.(t) ?? type ?? emptyLongDash;
};

const getStatusLabel = (status: ServiceAccount['status'], t: TFunction) => {
  return statusLabels[status]?.(t) ?? status ?? emptyLongDash;
};

const ActionCell: FC<
  UseColumnsProps & {
    data?: ServiceAccount;
  }
> = ({ access, data, onSelectAction, tableWrapperRef }) => {
  const { t } = useTranslation();
  const [above, setAbove] = useState(false);

  // KZN-17001: Add support for 'disabled' status for service accounts
  // const isDisabled = data?.status === 'disabled';
  const isIntegration = data?.account_type === 'service_integration';
  const isPluginIntegration =
    (data?.account_type as string) === 'service_integration_plugin';
  const hasApiTokenActions = isIntegration || isPluginIntegration;

  const options = useMemo(
    () =>
      [
        hasApiTokenActions && {
          value: 'view-token',
          label: t('View API Key(s)'),
        },
        hasApiTokenActions &&
          access.edit && {
            value: 'rotate-token',
            label: t('Rotate API Key'),
          },
        hasApiTokenActions &&
          access.edit &&
          data?.secondary_api_token_preview && {
            value: 'delete-secondary-token',
            label: t('Delete Secondary API Key'),
          },
        { value: 'view-history', label: t('View History') },
        // KZN-17001: Add support for 'disabled' status for service accounts
        //   access.edit && {
        //     value: isDisabled ? 'enable' : 'disable',
        //     label: isDisabled ? t('Enable') : t('Disable'),
        //   },
        isIntegration &&
          access.remove && { value: 'archive', label: t('Archive') },
      ].filter(Boolean) as { value: MenuAction; label: string }[],
    [access, data, hasApiTokenActions, isIntegration, t]
  );

  const handleChange = ({ value }: { value: MenuAction }) => {
    onSelectAction(value, data!);
  };

  const handleOpen = (
    _isOpen: boolean,
    targetRef: RefObject<HTMLButtonElement>
  ) => {
    if (tableWrapperRef.current && targetRef.current) {
      const { bottom } = tableWrapperRef.current.getBoundingClientRect();
      const { bottom: targetBottom } =
        targetRef.current.getBoundingClientRect();
      setAbove(bottom < targetBottom + options.length * OPTION_HEIGHT);
    }
  };

  return options.length ? (
    <StyledActionSizedCell
      minWidth={COLUMN_WIDTH.actions}
      padding="0px"
      data-qa="cell-actions"
    >
      <IconButtonAbsoluteMenu
        title={t('Service Account Actions')}
        sizing="dense"
        position="right"
        color={colorsButton.iconGray}
        onChange={handleChange}
        onOpen={handleOpen}
        options={options}
        above={above}
      >
        <Icon icon="three-dot" />
      </IconButtonAbsoluteMenu>
    </StyledActionSizedCell>
  ) : (
    <SizedCell
      minWidth={COLUMN_WIDTH.actions}
      padding="0px"
      data-qa="cell-no-actions"
    />
  );
};

const InlineNameCell: FC<{
  data: ServiceAccount;
  access: AccessStruct;
  onRefetch: () => void;
}> = ({ data, access, onRefetch }) => {
  const isPlugin = isPluginServiceAccount(data);
  const pluginApiName = getPluginApiName(data);

  const handleSubmit = useCallback(
    async (newValue: string) => {
      if (!data || newValue === data.name) return;
      await ServiceAccountService.updateServiceAccount(data.id, {
        name: newValue,
      });
      onRefetch();
    },
    [data, onRefetch]
  );

  if (!access.edit) {
    return (
      <StyledNameCellContent>
        <TextEllipsisWithTooltip style={{ flex: 1, minWidth: 0 }}>
          {data?.name}
        </TextEllipsisWithTooltip>
        {isPlugin ? <PluginIconIndicator appName={pluginApiName} /> : null}
      </StyledNameCellContent>
    );
  }

  return (
    <StyledNameCellContent>
      <TextInputInline
        value={data?.name ?? ''}
        onSubmit={handleSubmit}
        data-qa="cell-name-inline"
        readOnly={data?.account_type !== 'service_integration'}
        style={{ flex: 1, minWidth: 0 }}
      />
      {isPlugin ? <PluginIconIndicator appName={pluginApiName} /> : null}
    </StyledNameCellContent>
  );
};

const InlineApiNameCell: FC<{
  data: ServiceAccount;
  access: AccessStruct;
  onRefetch: () => void;
}> = ({ data, access, onRefetch }) => {
  const handleSubmit = useCallback(
    async (newValue: string) => {
      if (!data || newValue === data.api_name) return;
      await ServiceAccountService.updateServiceAccount(data.id, {
        api_name: newValue,
      });
      onRefetch();
    },
    [data, onRefetch]
  );

  if (!access.edit) {
    return <TextEllipsisWithTooltip>{data?.api_name}</TextEllipsisWithTooltip>;
  }

  return (
    <TextInputInline
      value={data?.api_name ?? ''}
      onSubmit={handleSubmit}
      data-qa="cell-api-name-inline"
      readOnly={data?.account_type !== 'service_integration'}
    />
  );
};

type InlinePermissionGroupsCellProps = {
  data?: ServiceAccount;
  access: AccessStruct;
  onRefetch: () => void;
};

const InlinePermissionGroupsCell: FC<InlinePermissionGroupsCellProps> = ({
  data,
  access,
  onRefetch,
}) => {
  const { t } = useTranslation();
  const [showNoGroupsConfirm, setShowNoGroupsConfirm] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const { mutate } = useMutation({
    mutationFn: async (groups: ServiceAccount['permission_groups']) => {
      if (!data) return;
      await ServiceAccountService.updateServiceAccount(data.id, {
        permission_groups: groups.map((g) => g.id),
      });
    },
    onSuccess: () => {
      onRefetch();
    },
  });

  const hasGroups = !!data?.permission_groups?.length;

  const handleSubmit = useCallback(
    async (groups: ServiceAccount['permission_groups']) => {
      if (groups.length === 0 && hasGroups) {
        setShowNoGroupsConfirm(true);
        return;
      }
      mutate(groups);
    },
    [mutate, hasGroups]
  );

  const isPermissionGroupsReadOnly =
    !access.edit ||
    (data?.account_type as string) === 'service_automationplugin';

  if (isPermissionGroupsReadOnly) {
    return (
      <TextEllipsisWithTooltip>
        {data?.permission_groups?.map((g) => g.name).join(', ') ??
          emptyLongDash}
      </TextEllipsisWithTooltip>
    );
  }

  return (
    <>
      <TeamMemberPermissionGroups
        key={resetKey}
        value={data?.permission_groups ?? []}
        onSubmit={handleSubmit}
      />
      <ConfirmationModal
        show={showNoGroupsConfirm}
        heading={t('No Permission Groups')}
        buttonText={t('Proceed')}
        onConfirm={() => {
          mutate([]);
          setShowNoGroupsConfirm(false);
        }}
        onHide={() => {
          setShowNoGroupsConfirm(false);
          setResetKey((prev) => prev + 1);
        }}
      >
        <KizenTypography>
          {t(
            "Without any permission groups, integrations using this service account's API key will no longer work. Are you sure?"
          )}
        </KizenTypography>
      </ConfirmationModal>
    </>
  );
};

export const useColumns = ({
  onSelectAction,
  access,
  tableWrapperRef,
}: UseColumnsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const onRefetch = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: serviceAccountsInfiniteQuery().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: runToolsAsOptionsQuery().queryKey,
    });
  }, [queryClient]);

  return useMemo(
    () => [
      {
        id: 'spacer',
        headCell: <SizedCell width="20px" />,
        cell: <SizedCell width="20px" />,
      },
      {
        id: 'name',
        headCell: (
          <SortableHeadCell
            by="name"
            // KZN-16715: Plugin App Service Accounts. Label will be 'Name / Plugin App'
            label={t('Name')}
            minWidth={COLUMN_WIDTH.name}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.name}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-name"
          />
        ),
        format: (_: string, row: ServiceAccount) => (
          <InlineNameCell data={row} access={access} onRefetch={onRefetch} />
        ),
      },
      {
        id: 'account_type',
        headCell: (
          <HeaderSizedCell
            label={t('Type')}
            minWidth={COLUMN_WIDTH.type}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.type}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-account-type"
          />
        ),
        format: (type: ServiceAccount['account_type']) => (
          <TextEllipsisWithTooltip>
            {getAccountTypeLabel(type, t)}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'status',
        headCell: (
          <HeaderSizedCell
            label={t('Status')}
            minWidth={COLUMN_WIDTH.status}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.status}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-status"
          />
        ),
        format: (status: ServiceAccount['status']) => (
          <StatusCell>
            <StatusDot status={status} />
            {getStatusLabel(status, t)}
          </StatusCell>
        ),
      },
      {
        id: 'api_name',
        headCell: (
          <HeaderSizedCell
            label={t('API Name')}
            minWidth={COLUMN_WIDTH.apiName}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.apiName}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-api-name"
          />
        ),
        format: (_: string, row: ServiceAccount) => (
          <InlineApiNameCell data={row} access={access} onRefetch={onRefetch} />
        ),
      },
      {
        id: 'primary_api_token_preview',
        headCell: (
          <HeaderSizedCell
            label={t('Primary API Key')}
            minWidth={COLUMN_WIDTH.token}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.token}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-primary-api-token-preview"
          />
        ),
        format: (token: string) => (
          <TextEllipsisWithTooltip>
            {token ?? emptyLongDash}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'permission_groups',
        headCell: (
          <HeaderSizedCell
            label={t('Permission Group(s)')}
            minWidth={COLUMN_WIDTH.permissionGroups}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.permissionGroups}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-permission-groups"
          />
        ),
        format: (
          _: ServiceAccount['permission_groups'],
          row: ServiceAccount
        ) => (
          <InlinePermissionGroupsCell
            data={row}
            access={access}
            onRefetch={onRefetch}
          />
        ),
      },
      {
        id: 'updated',
        headCell: (
          <HeaderSizedCell
            label={t('Date Updated')}
            minWidth={COLUMN_WIDTH.date}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.date}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-updated"
          />
        ),
        format: (date: string) => (
          <DateTimeInputInline value={date} readOnly showDateTooltip />
        ),
      },
      {
        id: 'actions',
        headCell: (
          <HeaderSizedCell
            label={t('Actions')}
            minWidth={COLUMN_WIDTH.actions}
            padding="0px"
            align="right"
            t={t}
          />
        ),
        cell: (
          <ActionCell
            onSelectAction={onSelectAction}
            access={access}
            tableWrapperRef={tableWrapperRef}
          />
        ),
      },
      {
        id: 'right-spacer',
        headCell: <SizedCell width="20px" />,
        cell: <SizedCell width="20px" />,
      },
    ],
    [access, onRefetch, onSelectAction, tableWrapperRef, t]
  );
};
